import { useState } from 'react';
import { Modal, Avatar, Typography, Button, Tag, Image, Space } from 'antd';
import {
    IconMessage,
    IconClose,
    IconUsers,
    IconCourse,
    IconTrophy,
    IconCrown,
} from './icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { RoleIcon, getApiFileUrl } from '../../utils/util';
import { formatLastSeen } from '../../utils/FormatDate';
import { getUserCredentials } from '../../core/auth';

const { Title, Text } = Typography;

/**
 * PublicProfile Modal Component
 * Premium profile card with global ranking and clean design.
 */
const PublicProfile = ({
    visible,
    onClose,
    user,
    onSendMessage,
    isCurrentUser = false,
    isMobile = false,
    onlineUsernames = [],
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [photoPreviewVisible, setPhotoPreviewVisible] = useState(false);

    if (!user) return null;

    const getRoleColor = (role) => {
        const colors = { STUDENT: 'blue', TEACHER: 'green', ADMIN: 'red' };
        return colors[role] || 'default';
    };

    const getRoleLabel = (role) => {
        if (!role) return '';
        return t(`roles.${role.toLowerCase()}`);
    };

    // Real-time online status: prefer onlineUsernames (WebSocket) over static server data
    const isOnline = onlineUsernames.length > 0
        ? onlineUsernames.includes(user.username)
        : (user.isOnline || user.online);
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
    const photoUrl = user.profilePhotoUrl ? getApiFileUrl(user.profilePhotoUrl) : null;
    const isStudent = user.role === 'STUDENT';
    const hasRanking = user.globalMockExamRank != null && user.totalMockExamParticipants != null && user.totalMockExamParticipants > 0;
    const isMythical = isStudent && hasRanking && user.globalMockExamRank === 1;

    // Determine if the viewed profile belongs to the current user (hide "send message" to self)
    const currentUser = getUserCredentials();
    const isSelf = isCurrentUser || (!!currentUser && (
        (user.id != null && String(currentUser.id) === String(user.id)) ||
        (!!user.username && currentUser.username === user.username)
    ));

    const handleSendMessage = () => {
        if (onSendMessage) {
            onSendMessage(user);
        } else {
            // Navigate to the correct role-based messages route
            const currentUser = getUserCredentials();
            const rolePrefix = currentUser?.roleName?.toLowerCase() || 'student';
            navigate(`/${rolePrefix}/messages`, { state: { openChatWithUser: user } });
        }
        onClose();
    };

    return (
        <>
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            centered
            width={isMobile ? '95%' : 600}
            closeIcon={<IconClose style={{ color: 'var(--color-text-tertiary)', fontSize: 14 }} />}
            styles={{
                body: { padding: 0 },
                content: { borderRadius: 16, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.12)' }
            }}
        >
            {/* Header */}
            <div style={{
                background: 'var(--color-bg-subtle)',
                borderBottom: '1px solid var(--color-border)',
                padding: isMobile ? '28px 20px 24px' : '32px 32px 28px',
                display: 'flex',
                alignItems: 'center',
                gap: isMobile ? 16 : 20,
            }}>
                {/* Avatar */}
                <div style={{ position: 'relative', flexShrink: 0 }}>
                    <Avatar
                        size={isMobile ? 76 : 88}
                        src={photoUrl}
                        icon={!photoUrl && <RoleIcon role={user.role} />}
                        style={{
                            backgroundColor: photoUrl ? 'transparent' : 'var(--color-bg-muted)',
                            border: '3px solid #fff',
                            boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                            cursor: photoUrl ? 'pointer' : 'default',
                            transition: 'transform 0.2s ease',
                        }}
                        onMouseEnter={(e) => { if (photoUrl) e.currentTarget.style.transform = 'scale(1.04)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                        onClick={() => { if (photoUrl) setPhotoPreviewVisible(true); }}
                    />
                    {/* Online dot */}
                    <div style={{
                        position: 'absolute',
                        bottom: 3,
                        right: 3,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: isOnline ? '#52c41a' : 'var(--color-text-tertiary)',
                        border: '2.5px solid var(--color-bg-subtle)',
                        transition: 'background-color 0.3s',
                    }} />
                </div>

                {/* Name & meta */}
                <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <Title
                            level={4}
                            style={{
                                margin: 0,
                                fontSize: isMobile ? 18 : 21,
                                fontWeight: 700,
                                lineHeight: 1.25,
                                letterSpacing: '-0.01em',
                                color: 'var(--color-text-primary)',
                            }}
                            ellipsis
                        >
                            {fullName || t('profile.unknownUser')}
                        </Title>
                        <Tag
                            color={getRoleColor(user.role)}
                            style={{ margin: 0, fontSize: 11, lineHeight: '20px', padding: '0 10px', borderRadius: 10, fontWeight: 500 }}
                        >
                            {getRoleLabel(user.role)}
                        </Tag>
                        {isMythical && (
                            <Tag className="mythical-badge" style={{
                                margin: 0,
                                fontSize: 10,
                                lineHeight: '16px',
                                padding: '0 6px',
                                borderRadius: 6,
                                fontWeight: 600,
                                fontFamily: "'JetBrains Mono', monospace",
                            }}>
                                {t('ranking.mythical')}
                            </Tag>
                        )}
                    </div>
                    <Text style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                        @{user.username}
                    </Text>
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <Text style={{ fontSize: 12, color: isOnline ? '#52c41a' : 'var(--color-text-tertiary)', fontWeight: 500 }}>
                            {isOnline
                                ? `● ${t('profile.online')}`
                                : user.lastSeen
                                    ? formatLastSeen(user.lastSeen)
                                    : ''
                            }
                        </Text>
                    </div>
                </div>
            </div>

            {/* Stats row - only for students */}
            {isStudent && (
            <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)' }}>
                <div style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '16px 8px',
                    borderRight: '1px solid var(--color-border)',
                }}>
                    <div style={{ fontSize: isMobile ? 20 : 22, fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1 }}>
                        {user.groupCount ?? 0}
                    </div>
                    <Text style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                        <IconUsers style={{ marginRight: 3 }} />
                        {t('profile.groups')}
                    </Text>
                </div>
                <div style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '16px 8px',
                    borderRight: '1px solid var(--color-border)',
                }}>
                    <div style={{ fontSize: isMobile ? 20 : 22, fontWeight: 700, color: 'var(--color-text-primary)', lineHeight: 1 }}>
                        {user.courseCount ?? 0}
                    </div>
                    <Text style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                        <IconCourse style={{ marginRight: 3 }} />
                        {t('profile.courses')}
                    </Text>
                </div>
                <div style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '16px 8px',
                }}>
                    {hasRanking ? (
                        <>
                            <div style={{
                                fontSize: isMobile ? 20 : 22,
                                fontWeight: 700,
                                color: user.globalMockExamRank <= 3 ? '#faad14' : 'var(--color-text-primary)',
                                lineHeight: 1,
                            }}>
                                {user.globalMockExamRank <= 3 ? (
                                    <Space size={2}>
                                        {user.globalMockExamRank === 1 && <IconCrown style={{ fontSize: 14, color: '#faad14' }} />}
                                        #{user.globalMockExamRank}
                                    </Space>
                                ) : (
                                    `#${user.globalMockExamRank}`
                                )}
                            </div>
                            <Text style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                                <IconTrophy style={{ marginRight: 3 }} />
                                {t('profile.globalRank')}
                            </Text>
                        </>
                    ) : (
                        <>
                            <div style={{ fontSize: isMobile ? 20 : 22, fontWeight: 700, color: 'var(--color-text-tertiary)', lineHeight: 1 }}>
                                —
                            </div>
                            <Text style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>
                                <IconTrophy style={{ marginRight: 3 }} />
                                {t('profile.globalRank')}
                            </Text>
                        </>
                    )}
                </div>
            </div>
            )}

            {/* Action - hidden when viewing your own profile */}
            {!isSelf && (
            <div style={{ padding: isMobile ? '16px 20px' : '20px 32px' }}>
                <Button
                    type="primary"
                    icon={<IconMessage />}
                    block
                    size="large"
                    onClick={handleSendMessage}
                    style={{
                        height: 44,
                        borderRadius: 10,
                        fontWeight: 600,
                        fontSize: 14,
                    }}
                >
                    {t('profile.sendMessage')}
                </Button>
            </div>
            )}
        </Modal>

        {/* Fullscreen photo preview */}
        {photoUrl && (
            <Image
                style={{ display: 'none' }}
                src={photoUrl}
                preview={{
                    visible: photoPreviewVisible,
                    onVisibleChange: (v) => setPhotoPreviewVisible(v),
                    mask: null,
                }}
            />
        )}

        {/* Mythical badge CSS */}
        <style>{`
            .mythical-badge {
                background-color: hsl(350, 95%, 62%) !important;
                animation: mythicalGlowProfile 5s linear infinite !important;
                color: #fff !important;
                border: none !important;
            }
            /* Uniform hue shift across the whole badge (Monkeytype-style). */
            @keyframes mythicalGlowProfile {
                from { filter: hue-rotate(0deg); }
                to { filter: hue-rotate(360deg); }
            }
        `}</style>
        </>
    );
};

export default PublicProfile;
